import { modalMapper } from ".";
import useDirection from "hooks/useDirection";
import { getModalHeader } from "./modalHeaders";
import { Suspense, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { store } from "redux/store";
import useBackToClose from "hooks/useBackToClose";
import { Box, CircularProgress, Modal } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { MODAL, modalActions } from "redux/slices/modal";
import { isHourlyFullScreen } from "./HourlyDestinationModal/fullScreen";
import CircleLoader from "components/atoms/CircleLoader";
import ErrorBoundary from "components/atoms/ErrorBoundary";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { useTranslation } from "react-i18next";
import "./AppModal.scss";

function AppModal() {
  const { t, i18n } = useTranslation("translation");
  const direction = useDirection();
  const dispatch = useAppDispatch();
  const type = useAppSelector((state) => state.modal.type);
  const open = useAppSelector((state) => state.modal.open);
  const width = useAppSelector((state) => state.modal.width);
  const loading = useAppSelector((state) => state.modal.loading);
  const data = useAppSelector((state) => state.modal.data);
  const mapped = getModalHeader(type, t, data);
  const title = mapped.title;
  const subtitle = mapped.subtitle;
  const preventDismiss = loading;

  /* Back closes the modal rather than changing the screen under it. */
  useBackToClose(open, () => dispatch(modalActions.closeModal()));

  /* And a screen change closes it: a modal belongs to the screen that
     opened it, and one left open over the next screen (a booking's edit
     form over the bookings list) acted on a booking no longer shown. */
  const { pathname } = useLocation();
  const lastPathname = useRef(pathname);
  useEffect(() => {
    if (lastPathname.current === pathname) return;
    lastPathname.current = pathname;
    if (store.getState().modal.open) dispatch(modalActions.closeModal());
  }, [pathname, dispatch]);

  /* On phones the hourly drop-off step is its own full-screen screen: no
     sheet around it, so the reader lands straight on the destination. */
  if (open && type === MODAL.HOURLY_DESTINATION && isHourlyFullScreen()) {
    return (
      <ErrorBoundary>
        <Suspense fallback={null}>{modalMapper[type]}</Suspense>
      </ErrorBoundary>
    );
  }

  return (
    <Modal
      open={open}
      disableScrollLock
      /* yelo-usernext's backdrop: a wash rather than MUI's flat 50% black,
         and a blur, so the sheet reads as lifted off the page. */
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(22, 12, 26, 0.52)",
          backdropFilter: "blur(4px)",
        },
      }}
      onClose={(_, reason) => {
        if (preventDismiss) return;
        if (reason !== "backdropClick") dispatch(modalActions.closeModal());
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1400,
      }}
    >
      <Box
        className={`app-modal-body${type ? ` app-modal-body--${String(type).toLowerCase()}` : ""}`}
        sx={{
          width: { xs: "calc(100% - 28px)", sm: "100%" },
          border: "none",
          maxWidth: { xs: "calc(100% - 28px)", sm: width },
          maxHeight: { xs: "96vh", md: "90vh" },
          overflowY: "auto",
          borderRadius: "16px",
          bgcolor: "background.paper",
          ":focus-visible": {
            outline: "none",
          },
        }}
      >
        <ErrorBoundary>
          <div className="app-modal-inner" style={{ minHeight: "123px" }}>
            {/* yelo-usernext's header: the title with its subtitle under it
                on the leading edge, and a round close on the trailing edge,
                pinned so it never takes the copy's width. */}
            <div
              className={[
                "app-modal-head",
                title ? "has-title" : "",
                subtitle ? "has-subtitle" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              dir={direction}
            >
              {title ? (
                <div className="app-modal-heading">
                  <h2>{title}</h2>
                  {subtitle ? (
                    <p className="app-modal-subtitle">{subtitle}</p>
                  ) : null}
                </div>
              ) : null}
              <button
                type="button"
                className="app-modal-close"
                aria-label={t("close") || "Close"}
                disabled={preventDismiss}
                onClick={() => {
                  if (preventDismiss) return;
                  dispatch(modalActions.closeModal());
                }}
              >
                <CloseRoundedIcon aria-hidden="true" fontSize="small" />
              </button>
            </div>
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  /* Over the sticky header (z 6 in AppModal.scss) too: the
                     whole sheet is busy, so its title and close dim with the
                     form instead of standing sharp above the veil. */
                  zIndex: 7,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(255, 255, 255, 0.72)",
                  borderRadius: "24px 24px 0 0",
                }}
              >
                <CircleLoader />
              </Box>
            )}
            {/* In the body's own flow. CircleLoader covers its nearest
                positioned box with a blur at z 9999, which here was the
                whole card — while the backdrop was still fading in. */}
            <Suspense
              fallback={
                <div className="app-modal-pending">
                  <CircularProgress size={28} />
                </div>
              }
            >
              {type && modalMapper[type]}
            </Suspense>
          </div>
        </ErrorBoundary>
      </Box>
    </Modal>
  );
}

export default AppModal;
